import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useFeature } from '@/hooks/useFeature';
import {
  DEFAULT_MIN_COVERAGE_PCT,
  deriveStationVolumes,
  MAX_CROSSFALL_TOLERANCE_PCT,
  MIN_CROSSFALL_BAND_WIDTH_M,
  MIN_CROSSFALL_TOLERANCE_PCT,
  refineStationVolumes,
  stationGridResolution,
  stationVolumesDefaults,
  stationVolumesPayload,
  stationVolumesZodShape,
  type StationVolumesFormValues,
  type StationVolumesInitialParams,
} from '@/lib/stationVolumes/dialogModel';
import { reportLocaleFor, SUPPORTED_REPORT_LOCALES } from '@/lib/reportLocales';
import {
  expectationDefaults,
  expectationPayload,
  expectationZodShape,
  refineExpectation,
  type ExpectationForm,
} from '@/lib/surfaceExpectation';
import { useDialog } from '@/providers/DialogProvider';
import { cn } from '@/lib/utils';
import { matplotlibRampOptions } from '@/lib/colormaps';
import { defaultRampName } from '@/lib/cutFillConvention';
import { startDeviationAnalysis } from '@/queries/deviation.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExpectationFields } from '@/components/ExpectationFields';
import { StationVolumesFields } from '@/components/StationVolumesFields';

type LayerData = LayersQuery['layers'][number];

/**
 * The ramp picker's options, oriented so that CUT (positive deviation) is
 * red (T4). One list, in `@/lib/colormaps`; `jet` is gone from it on
 * purpose — a rainbow over a signed quantity invents an ordering the data
 * does not have. `RdBu` here is the `_r` spelling: deviation's cut side is
 * the positive one, the opposite of a surface difference's, which is why
 * `RunSurfaceDifferenceDialog.tsx` uses the plain spelling for the same
 * name.
 */
const RAMP_OPTIONS = matplotlibRampOptions('deviation');

interface Props {
  sourceLayer: LayerData;
  candidateReferenceLayers: LayerData[];
  projectId: string;
  initialValues?: Partial<FormData>;
  /** The dock's "Re-run with new parameters" pre-fill for a deviation layer
   *  (Task 6) — seeds the station-volumes section from the previous run's
   *  `StationParams` instead of making the user retype them. */
  initialStationParams?: StationVolumesInitialParams;
}

/** `extends StationVolumesFormValues, ExpectationForm` rather than re-listing
 *  their fields: the shared sections, their zod fragments and this form can
 *  then never drift apart (spec §10.4; expectation task 11). */
interface FormData extends StationVolumesFormValues, ExpectationForm {
  name: string;
  referenceLayerId: string;
  boundaryLayerId: string;
  mode: 'vertical' | 'normal' | 'nearest' | 'horizontal';
  min: number;
  max: number;
  decimate: number;
  colormap: string;
  verticalThreshold: number;
  kNearestTriangles: number;
  signed: boolean;
  ifcName?: string;
  ifcTypes?: string;
  outTif: boolean;
  tifResolution: number;
  tifStat: 'mean' | 'min' | 'max' | 'count';
  /** Optional EPSG override for the output GeoTIFF's CRS. Empty = let the
   *  server fill in the project's own SRID (the normal case). Useful only
   *  when the project SRID is wrong/missing and you want to stamp a
   *  specific code without a project edit. */
  tifSrid: number | null;
  renderPerSurface: boolean;
  renderImageSize: number;
  renderColormap: string;
  volumeStats: boolean;
  volumeResolution: number;
  volumeTolerance: number;
  volumePlane: 'xy' | 'fitted';
  referenceMode: 'tin' | 'surfaces';
  referenceResolution: number;
  referenceCellStat: 'min' | 'mean' | 'median' | 'p05';
  referenceInterp: 'nearest' | 'bilinear';
  generateReport: boolean;
  reportTitle?: string;
  reportNotes?: string;
  reportLocale: 'en' | 'de' | 'es' | 'fr' | 'hu';
}

type ReferenceLayerLike = { type?: string | null; metadata?: unknown };

const metadataType = (layer: ReferenceLayerLike | undefined): string =>
  (layer?.metadata as { type?: string } | null | undefined)?.type ?? '';

/** IFC models — either the raw `.ifc` (legacy `IFC`) or the fragment-converted
 *  tileset (`FRAGMENT`, the current upload path). */
const isIfcModelLayer = (layer: ReferenceLayerLike | undefined): boolean =>
  layer?.type === 'MODEL' && ['IFC', 'FRAGMENT'].includes(metadataType(layer));

/** MODEL sub-types the deviation worker can read as a reference mesh. XGRIDS /
 *  SPLAT layers are gaussian splats with no surface to measure against. */
const DEVIATION_MODEL_REFERENCE_TYPES = ['IFC', 'FRAGMENT', 'LANDXML'];

/** Layers that can be offered as a deviation reference. Shared by every
 *  entry point that opens this dialog so the list stays consistent. */
export const isDeviationReferenceCandidate = (layer: ReferenceLayerLike): boolean => {
  if (layer.type === 'VECTOR' || layer.type === 'POINTCLOUD') return true;
  if (layer.type === 'MODEL') return DEVIATION_MODEL_REFERENCE_TYPES.includes(metadataType(layer));
  return false;
};

export function RunDeviationAnalysisDialog({
  sourceLayer,
  candidateReferenceLayers,
  projectId,
  initialValues,
  initialStationParams,
}: Props) {
  const { t, i18n } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [, setTrackers] = useAtom(deviationTrackingAtom);
  const [, setReportTrackers] = useAtom(reportTrackingAtom);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const alignments = useAtomValue(alignmentsAtom);
  // The station-volumes section stays visible on every tier — a reader has to
  // be able to see what the analysis offers before deciding to pay for it —
  // but its controls read as muted and the enable checkbox answers a click
  // with the upgrade dialog rather than the HTML `disabled` attribute.
  const stationGate = useFeature('analysis.stationVolumes');
  // The thickness/conformance question is its own PRO gate, independent of
  // the parent analysis (owner's ruling) — same shape as `stationGate` above.
  const expectationGate = useFeature('analysis.surfaceExpectation');

  const formSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().min(1),
          referenceLayerId: z.string().min(1, t('deviationAnalysis.referenceLayerRequired')),
          boundaryLayerId: z.string().default(''),
          mode: z.enum(['vertical', 'normal', 'nearest', 'horizontal']),
          min: z.number(),
          max: z.number(),
          decimate: z.number().int().min(1).default(1),
          colormap: z.string().default(defaultRampName('deviation')),
          verticalThreshold: z.number().min(0).max(1).default(0.25),
          kNearestTriangles: z.number().int().min(1).default(16),
          signed: z.boolean().default(true),
          ifcName: z.string().optional(),
          ifcTypes: z.string().optional(),
          outTif: z.boolean().default(false),
          tifResolution: z.number().positive().default(0.05),
          tifStat: z.enum(['mean', 'min', 'max', 'count']).default('mean'),
          tifSrid: z.number().int().positive().nullable().default(null),
          renderPerSurface: z.boolean().default(true),
          renderImageSize: z.number().int().min(256).max(8192).default(2048),
          renderColormap: z.string().default(defaultRampName('deviation')),
          volumeStats: z.boolean().default(true),
          volumeResolution: z.number().positive().default(0.05),
          volumeTolerance: z.number().positive().default(0.01),
          volumePlane: z.enum(['xy', 'fitted']).default('xy'),
          referenceMode: z.enum(['tin', 'surfaces']).default('surfaces'),
          referenceResolution: z.number().positive().default(0.05),
          referenceCellStat: z.enum(['min', 'mean', 'median', 'p05']).default('min'),
          referenceInterp: z.enum(['nearest', 'bilinear']).default('nearest'),
          generateReport: z.boolean().default(false),
          reportTitle: z.string().optional(),
          reportNotes: z.string().optional(),
          reportLocale: z.enum(SUPPORTED_REPORT_LOCALES).default('en'),
          ...stationVolumesZodShape,
          ...expectationZodShape,
        })
        .refine((d) => d.min < d.max, {
          message: t('deviationAnalysis.minMustBeLessThanMax'),
          path: ['max'],
        })
        .superRefine((d, ctx) => {
          refineStationVolumes(d, ctx);
          refineExpectation(d, ctx);
          // A thickness/match check on a DEVIATION run is only ever computed
          // inside `compute_station_volumes` — `deviation.py`'s expectation
          // block (`expect_mode != "NONE"`) lives there, and that whole call
          // is gated by `if station_spec is not None:` at the CLI entry
          // point. With station volumes off, the expectation the user just
          // configured is silently ignored: no error, no verdict, a run that
          // looks identical to NONE. That is worse than refusing the submit,
          // so this is hard validity — belongs here, in the dialog's own
          // `superRefine` (the only place that can see both fields), not in
          // `refineExpectation` (a pure function over the expectation fields
          // alone, deliberately not widened to read unrelated form state).
          //
          // Deliberately NOT mirrored in `RunSurfaceDifferenceDialog`:
          // `surface_difference.py` computes `stats["expectation"]`
          // grid-wide, independent of station volumes, so an expectation
          // without them is a perfectly valid run on that side.
          if (d.expectMode !== 'NONE' && !d.stationVolumesEnabled) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['stationVolumesEnabled'],
              message: 'stationVolumesRequiredForExpectation',
            });
          }
        }),
    [t],
  );

  // Smart default for `referenceMode`. VECTOR layers (GeoJSON polygons) are
  // typically several walls / surfaces — `surfaces` mode produces a CSV
  // breakdown and per-surface renders. MODEL layers (DXF / IFC) are
  // typically one continuous TIN — `tin` mode does a single integration.
  const defaultReferenceMode: 'tin' | 'surfaces' = candidateReferenceLayers[0]?.type === 'VECTOR' ? 'surfaces' : 'tin';

  const defaultReportLocale = reportLocaleFor(i18n.language);

  const defaultName = `${sourceLayer.name} vs ${candidateReferenceLayers[0]?.name ?? ''}`.trim();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultName,
      referenceLayerId: candidateReferenceLayers[0]?.id ?? '',
      boundaryLayerId: '',
      mode: 'vertical',
      min: -0.05,
      max: 0.05,
      decimate: 1,
      colormap: defaultRampName('deviation'),
      verticalThreshold: 0.25,
      kNearestTriangles: 16,
      signed: true,
      ifcName: '',
      ifcTypes: '',
      outTif: false,
      tifResolution: 0.05,
      tifStat: 'mean',
      tifSrid: null,
      renderPerSurface: true,
      renderImageSize: 2048,
      renderColormap: defaultRampName('deviation'),
      volumeStats: true,
      volumeResolution: 0.05,
      volumeTolerance: 0.01,
      volumePlane: 'xy',
      referenceMode: defaultReferenceMode,
      referenceResolution: 0.05,
      referenceCellStat: 'min',
      referenceInterp: 'nearest',
      generateReport: false,
      reportTitle: defaultName,
      reportNotes: '',
      reportLocale: defaultReportLocale,
      // Includes the gate on `stationVolumesEnabled`: the dock's "Re-run with
      // new parameters" always supplies `initialStationParams`, so without it
      // a locked user would land on a fully live, submittable station section.
      ...stationVolumesDefaults(initialStationParams, alignments, stationGate.allowed),
      // NONE by default. Note `...initialValues` is spread AFTER this, so a
      // caller-supplied `initialValues.expectMode` COULD in principle still
      // seed a non-NONE value here even while locked — this default alone
      // does not rule that out. What actually rules it out is
      // `ExpectationFields`' own gate-lapse effect, which resets
      // `expectMode` to NONE whenever the gate is closed, including on the
      // very first render (fix round 2, item 1 — an earlier version of this
      // comment claimed the spread order made this unreachable, which was
      // not true).
      ...expectationDefaults(),
      ...initialValues,
    },
  });

  // Re-default the referenceMode when the user picks a different reference
  // layer — VECTOR → surfaces, MODEL → tin. Only applies if the user hasn't
  // explicitly touched referenceMode (Form's `formState.dirtyFields`); we
  // don't want to clobber a deliberate override.
  const watchedReferenceLayerId = form.watch('referenceLayerId');
  const allLayers = useAtomValue(layersAtom);
  const boundaryCandidates = useMemo(
    () => allLayers.filter((l) => l.type === 'VECTOR' && l.id !== sourceLayer.id && l.id !== watchedReferenceLayerId),
    [allLayers, sourceLayer.id, watchedReferenceLayerId],
  );
  const watchedBoundaryLayerId = form.watch('boundaryLayerId');
  useEffect(() => {
    if (watchedBoundaryLayerId && !boundaryCandidates.some((l) => l.id === watchedBoundaryLayerId)) {
      form.setValue('boundaryLayerId', '');
    }
  }, [watchedBoundaryLayerId, boundaryCandidates, form]);
  useEffect(() => {
    const refLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
    if (!refLayer) return;
    if (refLayer.type === 'POINTCLOUD') {
      // PC references only support vertical mode + tin reference-mode.
      if (form.getValues('mode') !== 'vertical') {
        form.setValue('mode', 'vertical', { shouldDirty: false });
      }
      if (form.getValues('referenceMode') !== 'tin') {
        form.setValue('referenceMode', 'tin', { shouldDirty: false });
      }
      // PC<>PC is the earthworks / change-detection use case where the
      // tolerance-band defaults (±5 cm) are too narrow — typical "removed
      // material" deviations land in the ±0.5–2 m range and a tight band
      // saturates the colorbar, making visualisation misleading. Widen
      // when the user picks a PC reference and hasn't already set it.
      if (!form.formState.dirtyFields.min && form.getValues('min') === -0.05) {
        form.setValue('min', -1.0, { shouldDirty: false });
      }
      if (!form.formState.dirtyFields.max && form.getValues('max') === 0.05) {
        form.setValue('max', 1.0, { shouldDirty: false });
      }
      // Same logic for the volume tolerance: 1 cm is far too tight for
      // earthworks scale; 5 cm is a more realistic "is this within the
      // build envelope" threshold.
      if (!form.formState.dirtyFields.volumeTolerance && form.getValues('volumeTolerance') === 0.01) {
        form.setValue('volumeTolerance', 0.05, { shouldDirty: false });
      }
      return;
    }
    if (isIfcModelLayer(refLayer)) {
      // deviation.py can't run vertical / normal against IFC solids (no 2.5D
      // XY-containment lookup) — snap to nearest, the closest equivalent,
      // unless the user already sits on a valid mode.
      const mode = form.getValues('mode');
      if (mode !== 'nearest' && mode !== 'horizontal') {
        form.setValue('mode', 'nearest', { shouldDirty: false });
      }
    }
    if (form.formState.dirtyFields.referenceMode) return;
    const next: 'tin' | 'surfaces' = refLayer.type === 'VECTOR' ? 'surfaces' : 'tin';
    if (form.getValues('referenceMode') !== next) {
      form.setValue('referenceMode', next, { shouldDirty: false });
    }
  }, [watchedReferenceLayerId, candidateReferenceLayers, form]);

  // Update the suggested name when the reference layer changes, but only if
  // the user hasn't manually edited the name field.
  useEffect(() => {
    if (form.formState.dirtyFields.name) return;
    const refLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
    if (!refLayer) return;
    const suggested = `${sourceLayer.name} vs ${refLayer.name}`;
    if (form.getValues('name') !== suggested) {
      form.setValue('name', suggested, { shouldDirty: false });
    }
  }, [watchedReferenceLayerId, candidateReferenceLayers, form, sourceLayer.name]);

  const svValues: StationVolumesFormValues = {
    stationVolumesEnabled: form.watch('stationVolumesEnabled'),
    alignmentKey: form.watch('alignmentKey'),
    stationInterval: form.watch('stationInterval'),
    swathLeft: form.watch('swathLeft'),
    swathRight: form.watch('swathRight'),
    swathLinked: form.watch('swathLinked'),
    stationFromText: form.watch('stationFromText'),
    stationToText: form.watch('stationToText'),
    offsetBin: form.watch('offsetBin'),
    crossfallEnabled: form.watch('crossfallEnabled'),
    crossfallLeftEnabled: form.watch('crossfallLeftEnabled'),
    crossfallRightEnabled: form.watch('crossfallRightEnabled'),
    crossfallLeftFrom: form.watch('crossfallLeftFrom'),
    crossfallLeftTo: form.watch('crossfallLeftTo'),
    crossfallRightFrom: form.watch('crossfallRightFrom'),
    crossfallRightTo: form.watch('crossfallRightTo'),
    crossfallLinked: form.watch('crossfallLinked'),
    crossfallTolerancePct: form.watch('crossfallTolerancePct'),
  };
  // The worker bins at max(volumeResolution, 0.25 m) (spec §10.2 step 2), so
  // the live estimate — bin count, corridor area, cross-section size — has to
  // be sized on that number and not on the 5 cm volume grid, or it describes
  // a run nobody is going to make.
  const stationResolution = stationGridResolution(form.watch('volumeResolution'));
  const svAlignment = alignments.get(svValues.alignmentKey);
  // One derivation feeds both the section's messages and the Run button, so
  // they cannot disagree about what is blocking (spec §10.4).
  const svDerived = useMemo(
    () => deriveStationVolumes(svAlignment, svValues, stationResolution),
    // Each field individually: `svValues` is a fresh object every render.
    // `swathLinked` is deliberately absent — the link is a UI affordance the
    // derivation never reads.
    [
      svAlignment,
      stationResolution,
      svValues.stationVolumesEnabled,
      svValues.stationInterval,
      svValues.swathLeft,
      svValues.swathRight,
      svValues.stationFromText,
      svValues.stationToText,
      svValues.offsetBin,
      // The crossfall fields the derivation reads. `crossfallLinked` is
      // deliberately absent for the same reason `swathLinked` is — it is a UI
      // affordance the derivation never looks at.
      svValues.crossfallEnabled,
      svValues.crossfallLeftEnabled,
      svValues.crossfallRightEnabled,
      svValues.crossfallLeftFrom,
      svValues.crossfallLeftTo,
      svValues.crossfallRightFrom,
      svValues.crossfallRightTo,
      svValues.crossfallTolerancePct,
    ],
  );

  /** `StationVolumesFields` reports what the user typed; the form decides what
   *  it means. One `setValue` per field rather than a loop over
   *  `Object.entries`, which loses the key→value type pairing and would need a
   *  cast to put it back together. */
  const applyStation = useCallback(
    (patch: Partial<StationVolumesFormValues>) => {
      // Spec §10.4: station volumes are a second pass over the volume grid, so
      // ticking them turns volume statistics on — and pins the plane to world
      // XY, the only plane with a station axis — rather than failing later in
      // the resolver over a checkbox the user never saw. Written BEFORE
      // `stationVolumesEnabled` so the mirror effect below, which unticks the
      // section whenever those two are wrong, can never observe the
      // half-applied state regardless of `setValue`'s notification timing.
      if (patch.stationVolumesEnabled === true) {
        // T2: horizontal mode measures sideways to a wall, so there is no
        // vertical thickness to bin and `deviation.py` refuses the pairing
        // (exit 8). Refused here rather than in the effect below so a click
        // that arrives through the section's own checkbox cannot flip
        // `volumeStats` back on for one render before being undone.
        if (form.getValues('mode') === 'horizontal') return;
        if (!form.getValues('volumeStats')) form.setValue('volumeStats', true, { shouldDirty: true });
        if (form.getValues('volumePlane') !== 'xy') form.setValue('volumePlane', 'xy', { shouldDirty: true });
      }
      if (patch.stationVolumesEnabled !== undefined)
        form.setValue('stationVolumesEnabled', patch.stationVolumesEnabled);
      if (patch.alignmentKey !== undefined) form.setValue('alignmentKey', patch.alignmentKey);
      if (patch.stationInterval !== undefined) form.setValue('stationInterval', patch.stationInterval);
      if (patch.swathLeft !== undefined) form.setValue('swathLeft', patch.swathLeft);
      if (patch.swathRight !== undefined) form.setValue('swathRight', patch.swathRight);
      if (patch.swathLinked !== undefined) form.setValue('swathLinked', patch.swathLinked);
      if (patch.stationFromText !== undefined) form.setValue('stationFromText', patch.stationFromText);
      if (patch.stationToText !== undefined) form.setValue('stationToText', patch.stationToText);
      if (patch.offsetBin !== undefined) form.setValue('offsetBin', patch.offsetBin);
      // Linked swath mirroring lives with the form, not the field component:
      // the component reports one side, the form decides what that means for
      // the other — the same one-liner both dialogs use.
      if (patch.swathLeft !== undefined && form.getValues('swathLinked')) form.setValue('swathRight', patch.swathLeft);
      if (patch.swathRight !== undefined && form.getValues('swathLinked')) form.setValue('swathLeft', patch.swathRight);
      if (patch.crossfallEnabled !== undefined) form.setValue('crossfallEnabled', patch.crossfallEnabled);
      if (patch.crossfallLeftEnabled !== undefined)
        form.setValue('crossfallLeftEnabled', patch.crossfallLeftEnabled);
      if (patch.crossfallRightEnabled !== undefined)
        form.setValue('crossfallRightEnabled', patch.crossfallRightEnabled);
      if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallLeftFrom', patch.crossfallLeftFrom);
      if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallLeftTo);
      if (patch.crossfallRightFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallRightFrom);
      if (patch.crossfallRightTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallRightTo);
      if (patch.crossfallLinked !== undefined) form.setValue('crossfallLinked', patch.crossfallLinked);
      if (patch.crossfallTolerancePct !== undefined)
        form.setValue('crossfallTolerancePct', patch.crossfallTolerancePct);
      // Linked bands mirror the way the linked swath does, and for the same
      // reason: the component reports ONE side, the form decides what that
      // means for the other, so all three dialogs get it from one place.
      if (form.getValues('crossfallLinked')) {
        if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallLeftFrom);
        if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallLeftTo);
        if (patch.crossfallRightFrom !== undefined) form.setValue('crossfallLeftFrom', patch.crossfallRightFrom);
        if (patch.crossfallRightTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallRightTo);
      }
    },
    [form],
  );

  const mutation = useMutation({
    mutationFn: async (vars: FormData) => {
      // Only send `referenceMode` if the user explicitly picked it. The
      // form has a smart UI-default per layer type (VECTOR → surfaces,
      // MODEL → tin) so the dropdown shows something sensible, but if
      // the user never touched it we want the SERVER to apply its
      // feature-count-aware default — otherwise a TIN stored as a 4000-
      // feature GeoJSON gets routed to surfaces mode and triggers the
      // per-surface render cap. Sending `undefined` here lets preprocess
      // count features and pick `tin` automatically.
      const referenceModeDirty = !!form.formState.dirtyFields.referenceMode;
      // Only forward the IFC filters if the chosen reference layer is actually
      // an IFC model — otherwise stale form values from a previously selected
      // IFC reference would silently end up in the request.
      const submittedRefLayer = candidateReferenceLayers.find((l) => l.id === vars.referenceLayerId);
      const submittedRefIsIfc =
        submittedRefLayer?.type === 'MODEL' &&
        ['IFC', 'FRAGMENT'].includes((submittedRefLayer.metadata as { type?: string } | null)?.type ?? '');
      const submittedRefIsPc = submittedRefLayer?.type === 'POINTCLOUD';
      // A report is only actually produced for a GeoJSON / point-cloud
      // reference (D56). The form gate below keeps the box off for a model
      // reference; this re-read is what guarantees no `reportOptions` rides
      // along from a pre-filled or mid-edit form.
      const reportRequested = vars.generateReport;
      // When generating a PDF report, per-surface renders must be enabled so
      // the report's image section has content. Force it true regardless of
      // what the user has set for renderPerSurface in the form.
      const effectiveRenderPerSurface = reportRequested ? true : vars.renderPerSurface;
      return execute(startDeviationAnalysis, {
        input: {
          sourceLayerId: sourceLayer.id,
          referenceLayerId: vars.referenceLayerId,
          boundaryLayerId: vars.boundaryLayerId || undefined,
          name: vars.name || undefined,
          params: {
            mode: vars.mode,
            min: vars.min,
            max: vars.max,
            decimate: vars.decimate,
            colormap: vars.colormap,
            verticalThreshold: vars.verticalThreshold,
            kNearestTriangles: vars.kNearestTriangles,
            signed: vars.signed,
            ifcName: submittedRefIsIfc ? vars.ifcName || undefined : undefined,
            ifcTypes: submittedRefIsIfc ? vars.ifcTypes || undefined : undefined,
            outTif: vars.outTif,
            tifResolution: vars.tifResolution,
            tifStat: vars.tifStat,
            // null → server fills with project SRID. Sending undefined here
            // would also work but explicit null reads more deliberately.
            tifSrid: vars.tifSrid ?? null,
            renderPerSurface: effectiveRenderPerSurface,
            renderImageSize: vars.renderImageSize,
            renderColormap: vars.renderColormap,
            volumeStats: vars.volumeStats,
            volumeResolution: vars.volumeResolution,
            volumeTolerance: vars.volumeTolerance,
            volumePlane: vars.volumePlane,
            referenceMode: referenceModeDirty ? vars.referenceMode : undefined,
            referenceResolution: submittedRefIsPc ? vars.referenceResolution : undefined,
            referenceCellStat: submittedRefIsPc ? vars.referenceCellStat : undefined,
            referenceInterp: submittedRefIsPc ? vars.referenceInterp : undefined,
          },
          reportOptions: reportRequested
            ? {
                generate: true,
                title: vars.reportTitle?.trim() || undefined,
                notes: vars.reportNotes?.trim() || undefined,
                locale: vars.reportLocale,
              }
            : undefined,
          // Re-read the gate here, not the form flag: it can lapse while the
          // dialog is open and the mutation costs real compute. `??
          // undefined` turns `expectationPayload`'s NONE-mode `null` into an
          // ABSENT key, so an ordinary run's payload is byte-identical to
          // today's, not `expectation: null`.
          //
          // Also gated on `stationGate.allowed` (§8.1a): `deviation.py` only
          // computes the expectation inside `compute_station_volumes`, so an
          // expectation sent without station volumes is silently ignored
          // server-side — exactly the "setting evaporates" case this dialog's
          // blocking rule exists to prevent. The two gates are read
          // independently and either can lapse on its own while the dialog is
          // open, so this AND is deliberate coupling, not redundancy — do not
          // drop it as a simplification.
          expectation:
            expectationGate.allowed && stationGate.allowed ? (expectationPayload(vars) ?? undefined) : undefined,
          // Re-read the gate here, not the form flag: `initialStationParams`
          // can seed it true and a subscription can lapse while the dialog is
          // open. The mutation costs real compute, so the gate has the last
          // word.
          //
          // `svDerived` is reused rather than recomputed (D17): it was derived
          // from the same form state `vars` carries, so a second
          // `deriveStationVolumes` call here could only ever disagree.
          stationVolumes: stationGate.allowed
            ? stationVolumesPayload(vars, svDerived, initialStationParams?.minCoveragePct ?? DEFAULT_MIN_COVERAGE_PCT)
            : undefined,
        },
      });
    },
    onSuccess: (data, vars) => {
      const batchId = data.startDeviationAnalysis;
      const toastId = toast.loading(t('deviationAnalysis.runningToast', { name: sourceLayer.name }), {
        duration: Infinity,
      });
      // Snapshot the layer IDs already derived from this source. The hook
      // resolves the toast when a NEW derived layer (one whose id isn't in
      // this set) appears — this is robust to client/server clock skew, which
      // bit us with the previous `createdAt >= startedAt` comparison. Pulled
      // straight from the React Query cache so we don't have to re-fetch.
      const cachedLayers = queryClient.getQueryData<{ layers: LayerData[] }>(['layers', projectId]);
      const existingDerivedLayerIds = (cachedLayers?.layers ?? [])
        .filter((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          return meta.derivedFrom === sourceLayer.id;
        })
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: sourceLayer.id,
          sourceLayerName: sourceLayer.name,
          existingDerivedLayerIds,
        },
      ]);
      // The worker chains the PDF for every reference type (`canChainDeviationReport`,
      // ruling D63) — either from the RENDER child or directly — so a ticked box
      // always ends in a document and the tracker can be pushed unconditionally.
      if (vars.generateReport) {
        const reportTitle = vars.reportTitle?.trim() || vars.name;
        setReportTrackers((prev) => [...prev, { toastId: undefined, batchId, projectId, title: reportTitle }]);
      }
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('deviationAnalysis.failedToStart'));
    },
  });

  const watchedMode = form.watch('mode');
  const isHorizontal = watchedMode === 'horizontal';
  const isNearest = watchedMode === 'nearest';
  const watchedGenerateReport = form.watch('generateReport');
  // PDF report is gated on volume stats — without `volumeStats: true` the
  // deviation worker doesn't compute volume.* fields, so the report would
  // show '—' for every metric. If the user turns volume stats off while
  // generateReport is checked, auto-uncheck it so the submission isn't
  // silently broken.
  const watchedVolumeStats = form.watch('volumeStats');
  useEffect(() => {
    if (!watchedVolumeStats && form.getValues('generateReport')) {
      form.setValue('generateReport', false, { shouldDirty: true });
    }
  }, [watchedVolumeStats, form]);

  // The reverse coupling, mirroring the PDF-report one directly above.
  // Turning volume statistics off leaves nothing to bin, and a PCA-fitted
  // plane has no station axis at all — the resolver rejects both pairings —
  // so the station section goes with them instead of silently submitting a
  // request that comes back as an error about a control the user never saw.
  const watchedVolumePlane = form.watch('volumePlane');
  useEffect(() => {
    if ((!watchedVolumeStats || watchedVolumePlane !== 'xy') && form.getValues('stationVolumesEnabled')) {
      form.setValue('stationVolumesEnabled', false, { shouldDirty: true });
    }
  }, [watchedVolumeStats, watchedVolumePlane, form]);

  // T2. A volume is a VERTICAL quantity: every other mode has a design
  // elevation under the cell to difference against, horizontal mode
  // deliberately measures the distance sideways to a wall and has none.
  // `deviation.py` refuses both pairings (exit 8), so switching to horizontal
  // clears them here rather than letting the user submit a run that fails
  // minutes later. `volumeStats` going false takes `stationVolumesEnabled` and
  // `generateReport` with it through the two effects above; both are cleared
  // explicitly anyway so the state is right in one render either way.
  useEffect(() => {
    if (!isHorizontal) return;
    if (form.getValues('volumeStats')) form.setValue('volumeStats', false, { shouldDirty: true });
    if (form.getValues('stationVolumesEnabled')) form.setValue('stationVolumesEnabled', false, { shouldDirty: true });
  }, [isHorizontal, form]);

  // IFC element-name / element-type filters only make sense when the reference
  // layer is an IFC model. The backend stores converted IFC files with
  // `metadata.type === 'FRAGMENT'`; some legacy uploads still use 'IFC'.
  const watchedReferenceLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
  const referenceLayerIsIfc =
    watchedReferenceLayer?.type === 'MODEL' &&
    (watchedReferenceLayer.metadata as { type?: string } | null)?.type !== undefined &&
    ['IFC', 'FRAGMENT'].includes((watchedReferenceLayer.metadata as { type: string }).type);
  const referenceLayerIsPointcloud = watchedReferenceLayer?.type === 'POINTCLOUD';
  /** D56: the chained PDF only exists for a GeoJSON / point-cloud reference. */
  // Defence in depth, mirroring `RunSurfaceDifferenceDialog`: Run is not
  // disabled by form validity, so an invalid value in a field the user has
  // since collapsed out of view (the "Advanced options" block) would make the
  // button a silent no-op. Maps the schema's custom issue `message`s (tags
  // like 'intervalRange', not literal text) back to real copy where one
  // exists; anything else falls back to the generic "could not start" toast.
  const onInvalid = (errors: FieldErrors<FormData>) => {
    const messages: Record<string, string> = {
      alignmentRequired: t('stationVolumes.dialog.alignmentRequired'),
      swathZero: t('stationVolumes.dialog.swathZero'),
      intervalRange: t('stationVolumes.errors.intervalRange'),
      swathRange: t('stationVolumes.errors.swathRange'),
      offsetBinRange: t('stationVolumes.errors.offsetBinRange'),
      crossfallBand: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
      crossfallWidth: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
      crossfallSwath: t('stationVolumes.errors.crossfallSwath'),
      crossfallNoBand: t('stationVolumes.errors.crossfallNoBand'),
      crossfallTolerance: t('stationVolumes.errors.crossfallTolerance', {
        min: MIN_CROSSFALL_TOLERANCE_PCT,
        max: MAX_CROSSFALL_TOLERANCE_PCT,
      }),
      thicknessRequired: t('expectation.errors.thicknessRequired'),
      toleranceRequired: t('expectation.errors.toleranceRequired'),
      surfaceRequired: t('expectation.errors.surfaceRequired'),
      sameSurface: t('expectation.errors.sameSurface'),
      toleranceNegative: t('expectation.errors.toleranceNegative'),
      failAreaPctRange: t('expectation.errors.failAreaPctRange'),
      stationVolumesRequiredForExpectation: t('expectation.errors.stationVolumesRequiredForExpectation'),
    };
    const first = Object.values(errors).find(
      (e): e is { message?: string } => !!e && typeof e === 'object' && 'message' in e,
    );
    toast.error((first?.message && messages[first.message]) || t('deviationAnalysis.failedToStart'));
  };

  if (candidateReferenceLayers.length === 0) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('deviationAnalysis.title')}</DialogTitle>
        </DialogHeader>
        <p className='py-4 text-sm text-slate-700 dark:text-stone-200'>{t('deviationAnalysis.noReferenceLayers')}</p>
        <DialogFooter>
          <Button variant='outline' onClick={() => dismissActiveDialog()}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      {/* `min-w-0` on the header lets its grid track shrink below the
          intrinsic width of a very long source-layer name — without it,
          the grid's default `min-width: auto` pegs the dialog's width to
          the name and the form inputs overflow. `break-words` then takes
          effect on the description text. */}
      <DialogHeader className='min-w-0'>
        <DialogTitle>{t('deviationAnalysis.title')}</DialogTitle>
        <DialogDescription className='break-words'>
          {t('deviationAnalysis.sourceLabel')}: <strong>{sourceLayer.name}</strong>
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((vars) => mutation.mutate(vars), onInvalid)}
          className='flex max-h-[80vh] flex-col'>
          <div className='flex-1 space-y-4 overflow-y-auto px-1 py-1'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.outputName')}</FormLabel>
                  <FormControl>
                    <Input type='text' {...field} />
                  </FormControl>
                  <FormDescription>{t('deviationAnalysis.outputNameHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='referenceLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.referenceLayer')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {candidateReferenceLayers.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} ({l.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='boundaryLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('boundary.label')}</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                    value={field.value || '__none__'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='__none__'>{t('boundary.none')}</SelectItem>
                      {boundaryCandidates.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('boundary.help')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Top-level, not inside "Advanced options": a corridor is not an
                advanced knob — it changes what the run produces. */}
            {/* T2: horizontal mode has no vertical volume to bin, so the whole
                section is inert while it is selected. A native `<fieldset
                disabled>` rather than a `disabled` prop on the shared
                component — it is the deviation dialog's own restriction, not
                something the surface-difference and layer-stack dialogs that
                share `StationVolumesFields` know or care about. */}
            {isHorizontal && (
              <p data-testid='dev-no-vertical-volume' className='text-sm text-slate-600 dark:text-stone-400'>
                {t('deviationAnalysis.horizontalNoVolume')}
              </p>
            )}
            <fieldset disabled={isHorizontal} className={cn('min-w-0', isHorizontal && 'opacity-50')}>
              <StationVolumesFields
                values={svValues}
                onChange={applyStation}
                resolution={stationResolution}
                derived={svDerived}
                gate={stationGate}
                referenceLayerId={watchedReferenceLayerId}
                enabledNote={
                  <p className='text-xs text-slate-600 dark:text-stone-400'>
                    {t('stationVolumes.dialog.volumeStatsAuto')}
                  </p>
                }
              />
            </fieldset>

            {/* SURFACES mode is surface-difference only (`allowSurfaces=false`):
                a deviation run's reference is already a single surface, and
                `deviation.py` refuses that mode at its argument parser. */}
            <ExpectationFields
              form={form}
              allowSurfaces={false}
              layers={allLayers}
              gate={expectationGate}
              effectiveResolutionM={stationResolution}
            />
            {/* Anchored to `stationVolumesEnabled` (fix round 1), the same
                shape as `RunSurfaceDifferenceDialog`'s `sameLayer` message —
                a translated `<p>`, never `<FormMessage/>`, since this issue
                is about a DEPENDENCY between two sections, not a bad value
                in either one on its own. */}
            {form.formState.errors.stationVolumesEnabled?.message === 'stationVolumesRequiredForExpectation' && (
              <p className='text-sm text-destructive'>{t('expectation.errors.stationVolumesRequiredForExpectation')}</p>
            )}

            {/* referenceMode is conceptually paired with the reference layer:
              one continuous TIN vs N independent surfaces. Auto-defaults
              from the reference layer's type (VECTOR → surfaces, MODEL →
              tin) and stays out of the way unless the user overrides.
              Hidden for POINTCLOUD references — those force tin-mode. */}
            {!referenceLayerIsPointcloud && (
              <FormField
                control={form.control}
                name='referenceMode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.referenceMode')}</FormLabel>
                    <Select
                      onValueChange={(v) => {
                        // Mark the field dirty so the layer-change effect
                        // doesn't clobber the user's explicit choice next
                        // time they swap reference layers.
                        field.onChange(v);
                      }}
                      value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='surfaces'>{t('deviationAnalysis.referenceModes.surfaces')}</SelectItem>
                        <SelectItem value='tin'>{t('deviationAnalysis.referenceModes.tin')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {field.value === 'surfaces'
                        ? t('deviationAnalysis.referenceModeHelp.surfaces')
                        : t('deviationAnalysis.referenceModeHelp.tin')}
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}

            {referenceLayerIsPointcloud ? (
              <div className='rounded border border-slate-200 p-3 text-sm text-slate-700 dark:border-stone-700 dark:text-stone-200'>
                {t('deviationAnalysis.modeLockedToVerticalForPc')}
              </div>
            ) : (
              <FormField
                control={form.control}
                name='mode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.mode')}</FormLabel>
                    {referenceLayerIsIfc && (
                      <FormDescription>{t('deviationAnalysis.modeLimitedForIfc')}</FormDescription>
                    )}
                    <FormControl>
                      {/* Controlled (`value`, not `defaultValue`) so the
                          reference-layer effect's programmatic mode changes
                          (IFC → nearest) show up in the radio group. */}
                      <RadioGroup onValueChange={field.onChange} value={field.value} className='flex flex-col gap-2'>
                        {(referenceLayerIsIfc
                          ? (['nearest', 'horizontal'] as const)
                          : (['vertical', 'normal', 'nearest', 'horizontal'] as const)
                        ).map((m) => (
                          <label key={m} className='flex cursor-pointer items-start gap-2'>
                            <RadioGroupItem value={m} id={`mode-${m}`} />
                            <div>
                              <div className='text-sm font-medium'>{t(`deviationAnalysis.modes.${m}.label`)}</div>
                              <div className='text-xs text-slate-600 dark:text-stone-400'>
                                {t(`deviationAnalysis.modes.${m}.description`)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <div className='grid grid-cols-2 gap-3'>
              <FormField
                control={form.control}
                name='min'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.min')}</FormLabel>
                    <FormControl>
                      <NumericField step='0.001' value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='max'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.max')}</FormLabel>
                    <FormControl>
                      <NumericField step='0.001' value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* T1: the range is a DISPLAY band. It decides the colouring and
                which points survive into the output cloud; it no longer
                decides what the volumes integrate, and a user who reads it as
                a physical boundary sets ±5 cm and wonders where their 30 cm
                overbuild went. */}
            <FormDescription className='-mt-1'>{t('deviationAnalysis.rangeIsDisplayOnly')}</FormDescription>

            <FormField
              control={form.control}
              name='decimate'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.decimate')}</FormLabel>
                  <FormControl>
                    <NumericField value={field.value} onChange={field.onChange} step='1' min='1' />
                  </FormControl>
                  <FormDescription>{t('deviationAnalysis.decimateHelp')}</FormDescription>
                </FormItem>
              )}
            />

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant='ghost' type='button' className='w-full justify-between'>
                  <span>{t('deviationAnalysis.advanced')}</span>
                  <ChevronDown className={`size-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className='space-y-3 pt-2'>
                {isHorizontal && (
                  <FormField
                    control={form.control}
                    name='verticalThreshold'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('deviationAnalysis.verticalThreshold')}</FormLabel>
                        <FormControl>
                          <NumericField value={field.value} onChange={field.onChange} step='0.05' min='0' max='1' />
                        </FormControl>
                        <FormDescription>{t('deviationAnalysis.verticalThresholdHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name='kNearestTriangles'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('deviationAnalysis.kNearestTriangles')}</FormLabel>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='1' min='1' />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {isNearest && (
                  <FormField
                    control={form.control}
                    name='signed'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.signed')}</FormLabel>
                      </FormItem>
                    )}
                  />
                )}
                {referenceLayerIsIfc && (
                  <>
                    <FormField
                      control={form.control}
                      name='ifcName'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.ifcName')}</FormLabel>
                          <FormControl>
                            <Input type='text' placeholder={t('deviationAnalysis.ifcNamePlaceholder')} {...field} />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.ifcNameHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='ifcTypes'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.ifcTypes')}</FormLabel>
                          <FormControl>
                            <Input type='text' placeholder={t('deviationAnalysis.ifcTypesPlaceholder')} {...field} />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.ifcTypesHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {referenceLayerIsPointcloud && (
                  <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                    <div className='mb-2 text-sm font-medium'>{t('deviationAnalysis.pcReferenceSection')}</div>
                    <div className='grid grid-cols-2 gap-3'>
                      <FormField
                        control={form.control}
                        name='referenceResolution'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('deviationAnalysis.referenceResolution')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='any' />
                            </FormControl>
                            <FormDescription>{t('deviationAnalysis.referenceResolutionHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='referenceCellStat'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('deviationAnalysis.referenceCellStat')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='min'>{t('deviationAnalysis.referenceCellStats.min')}</SelectItem>
                                <SelectItem value='mean'>{t('deviationAnalysis.referenceCellStats.mean')}</SelectItem>
                                <SelectItem value='median'>
                                  {t('deviationAnalysis.referenceCellStats.median')}
                                </SelectItem>
                                <SelectItem value='p05'>{t('deviationAnalysis.referenceCellStats.p05')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>{t('deviationAnalysis.referenceCellStatHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='referenceInterp'
                        render={({ field }) => (
                          <FormItem className='col-span-2'>
                            <FormLabel>{t('deviationAnalysis.referenceInterp')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='nearest'>
                                  {t('deviationAnalysis.referenceInterps.nearest')}
                                </SelectItem>
                                <SelectItem value='bilinear'>
                                  {t('deviationAnalysis.referenceInterps.bilinear')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>{t('deviationAnalysis.referenceInterpHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='outTif'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.outTif')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormDescription className='mt-1'>{t('deviationAnalysis.outTifHelp')}</FormDescription>
                </div>

                {form.watch('outTif') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='tifResolution'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.tifResolution')}</FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='any' />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.tifResolutionHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='tifStat'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.tifStat')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='mean'>{t('deviationAnalysis.tifStats.mean')}</SelectItem>
                              <SelectItem value='max'>{t('deviationAnalysis.tifStats.max')}</SelectItem>
                              <SelectItem value='min'>{t('deviationAnalysis.tifStats.min')}</SelectItem>
                              <SelectItem value='count'>{t('deviationAnalysis.tifStats.count')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.tifStatHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='tifSrid'
                      render={({ field }) => (
                        <FormItem className='col-span-2'>
                          <FormLabel>{t('deviationAnalysis.tifSrid')}</FormLabel>
                          <FormControl>
                            {/* Empty input → null → server falls back to the
                              project's own SRID, which is the right answer
                              99% of the time. Only fill this in when you
                              specifically want to override (e.g. project's
                              SRID is wrong and you can't edit it right now). */}
                            <Input
                              type='number'
                              step='1'
                              min='1'
                              placeholder={t('deviationAnalysis.tifSridPlaceholder')}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const v = e.target.value.trim();
                                if (v === '') {
                                  field.onChange(null);
                                } else {
                                  const n = parseInt(v, 10);
                                  field.onChange(Number.isFinite(n) && n > 0 ? n : null);
                                }
                              }}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.tifSridHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='renderPerSurface'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>
                          {referenceLayerIsPointcloud
                            ? t('deviationAnalysis.renderOverviewPc')
                            : t('deviationAnalysis.renderPerSurface')}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormDescription className='mt-1'>
                    {referenceLayerIsPointcloud
                      ? t('deviationAnalysis.renderOverviewPcHelp')
                      : t('deviationAnalysis.renderPerSurfaceHelp')}
                  </FormDescription>
                </div>

                {form.watch('renderPerSurface') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='renderImageSize'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.renderImageSize')}</FormLabel>
                          <FormControl>
                            <NumericField
                              value={field.value}
                              onChange={field.onChange}
                              step='256'
                              min='256'
                              max='8192'
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.renderImageSizeHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='renderColormap'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.renderColormap')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {/* Whatever a RE-RUN carried in is appended if it
                                  is no longer on the list (T4 dropped `jet`
                                  and the cut-blue orientation) — see
                                  `RunSurfaceDifferenceDialog.tsx`'s identical
                                  guard. A `Select` whose value is not among
                                  its items renders EMPTY, which reads as "no
                                  ramp set" and invites picking a different
                                  ramp than the run being repeated used. */}
                              {(RAMP_OPTIONS.some((c) => c.value === field.value) || !field.value
                                ? RAMP_OPTIONS
                                : [...RAMP_OPTIONS, { value: field.value, label: field.value }]
                              ).map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.renderColormapHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='volumeStats'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox
                            data-testid='dev-volume-stats'
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isHorizontal}
                          />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.volumeStats')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  {/* T2: the reason replaces the help text rather than sitting
                      next to it — a greyed checkbox with its usual description
                      under it explains nothing. */}
                  <FormDescription className='mt-1'>
                    {isHorizontal
                      ? t('deviationAnalysis.horizontalNoVolume')
                      : t('deviationAnalysis.volumeStatsHelp')}
                  </FormDescription>
                </div>

                {form.watch('volumeStats') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='volumeResolution'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.volumeResolution')}</FormLabel>
                          <FormControl>
                            {/* `step="any"` disables the browser's "value must
                              match min + N*step" check. Without it, a min of
                              0.001 + step of 0.01 made 0.05 itself invalid
                              and only a sparse set of "weird" values worked.
                              Zod's `.positive()` is the real validator. */}
                            <NumericField value={field.value} onChange={field.onChange} step='any' />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.volumeResolutionHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='volumeTolerance'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.volumeTolerance')}</FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='any' />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.volumeToleranceHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='volumePlane'
                      render={({ field }) => (
                        <FormItem className='col-span-2'>
                          <FormLabel>{t('deviationAnalysis.volumePlane')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='xy'>{t('deviationAnalysis.volumePlanes.xy')}</SelectItem>
                              <SelectItem value='fitted'>{t('deviationAnalysis.volumePlanes.fitted')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.volumePlaneHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* PDF report opt-in — gated on volume stats being enabled
                (the report's tables/colourbar/etc. require volume.* fields). */}
            <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
              <FormField
                control={form.control}
                name='generateReport'
                render={({ field }) => (
                  <FormItem className={`flex items-center gap-2 ${watchedVolumeStats ? '' : 'opacity-50'}`}>
                    <FormControl>
                      {/* A data condition, not a feature gate: the HTML
                          `disabled` is the right control here — there is
                          nothing behind the box to open, the way an upgrade
                          dialog sits behind a gated one. Same shape as
                          `RunSurfaceDifferenceDialog`'s station gate. */}
                      <Checkbox
                        data-testid='dev-generate-report'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!watchedVolumeStats}
                      />
                    </FormControl>
                    <FormLabel className='!mt-0'>{t('report.alsoGenerate')}</FormLabel>
                  </FormItem>
                )}
              />
              {!watchedVolumeStats && (
                <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>{t('report.requiresVolumeStats')}</p>
              )}
              {watchedGenerateReport && (
                <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>{t('report.alsoGenerateHint')}</p>
              )}
              {/* Spec §10.6: the chained report gains the corridor pages when
                  the run carried station volumes. */}
              {watchedGenerateReport && svValues.stationVolumesEnabled && (
                <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                  {t('stationVolumes.dialog.reportIncludesSection')}
                </p>
              )}
            </div>

            {watchedGenerateReport && (
              <div className='space-y-3'>
                <FormField
                  control={form.control}
                  name='reportTitle'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.titleLabel')}</FormLabel>
                      <FormControl>
                        <Input type='text' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='reportNotes'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.notesLabel')}</FormLabel>
                      <FormControl>
                        <Input type='text' {...field} value={field.value ?? ''} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='reportLocale'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.languageLabel')}</FormLabel>
                      <FormControl>
                        <select
                          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                          {...field}>
                          <option value='en'>English</option>
                          <option value='de'>Deutsch</option>
                          <option value='es'>Español</option>
                          <option value='fr'>Français</option>
                          <option value='hu'>Magyar</option>
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          <DialogFooter className='mt-4 shrink-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
            <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button data-testid='dev-submit' type='submit' disabled={mutation.isPending || svDerived.blocked}>
              {mutation.isPending ? t('deviationAnalysis.starting') : t('deviationAnalysis.run')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
